import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity as ActivityIcon,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Coins,
  FileText,
  FolderKanban,
  Globe2,
  Plus,
  Sparkles,
  Tag,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relativeTime } from "@/lib/relativeTime";

const STAT_ICONS = {
  brands: Tag,
  projects: FolderKanban,
  runs: ActivityIcon,
  findings: Sparkles,
};

const STATUS_TONES = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  running: "bg-indigo-50 text-indigo-700 border-indigo-200",
  queued: "bg-slate-50 text-slate-600 border-slate-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-amber-50 text-amber-700 border-amber-200",
};

function SkeletonLine({ className = "h-3 w-full" }) {
  return <div className={`animate-pulse rounded bg-slate-100 ${className}`} />;
}

function Card({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function StatCard({ k, value, sub, to }) {
  const { t } = useTranslation("dashboard");
  const Icon = STAT_ICONS[k];
  return (
    <Link
      to={to || "#"}
      data-testid={`dashboard-stat-${k}`}
      className="group flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-slate-500" strokeWidth={1.75} />
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {t(`stats.${k}.label`)}
        </p>
        <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900">
          {value ?? "—"}
        </p>
        {sub ? (
          <p className="mt-1 text-xs text-slate-500" data-testid={`dashboard-stat-${k}-sub`}>
            {sub}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONES[status] || STATUS_TONES.queued;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
      {status || "—"}
    </span>
  );
}

function ReliabilityBadge({ score }) {
  const pct = Math.round((Number(score) || 0) * 100);
  const tone =
    pct >= 70 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : pct >= 40 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>
      {pct}%
    </span>
  );
}

function PanelHeader({ title, action }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="font-display text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
      {action}
    </div>
  );
}

function EmptyHint({ children, testId }) {
  return (
    <p data-testid={testId} className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
      {children}
    </p>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation(["dashboard", "common", "nav"]);
  const { user, tenant } = useAuth();
  const lang = (i18n.resolvedLanguage || "en").split("-")[0];
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Credits is best-effort — we don't block the dashboard if it fails.
        const creditsP = api.get("/credits/me").catch(() => ({ data: null }));
        const [s, a, c] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/activity", { params: { page_size: 10 } }),
          creditsP,
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setActivity(a.data?.items || []);
        setCredits(c.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const canManage = user?.role === "owner" || user?.role === "admin";
  const counts = summary?.counts || {};
  const runs30dSub = useMemo(() => {
    const s = summary?.runs_status_30d || {};
    return [
      s.completed ? `${s.completed} ${t("dashboard:runs.completed")}` : null,
      s.running ? `${s.running} ${t("dashboard:runs.running")}` : null,
      s.failed ? `${s.failed} ${t("dashboard:runs.failed")}` : null,
    ].filter(Boolean).join(" · ");
  }, [summary, t]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {t("dashboard:tenantLabel")} · {tenant?.name}
          </span>
          <h1
            data-testid="dashboard-welcome"
            className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
          >
            {t("dashboard:welcome", { name: user?.name })}
          </h1>
          <p className="max-w-xl text-sm text-slate-500">{t("dashboard:tagline")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/brands"
            data-testid="dashboard-quick-new-brand"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("dashboard:quick.newBrand")}
          </Link>
          <Link
            to="/projects/new"
            data-testid="dashboard-quick-new-project"
            className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("dashboard:quick.newProject")}
          </Link>
        </div>
      </header>

      {error ? (
        <div data-testid="dashboard-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {t("dashboard:loadError")}
        </div>
      ) : null}

      {/* Stat row */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Card key={i} testId={`dashboard-stat-skeleton-${i}`} className="space-y-3">
              <SkeletonLine className="h-8 w-8" />
              <SkeletonLine className="h-3 w-20" />
              <SkeletonLine className="h-8 w-16" />
            </Card>
          ))
        ) : (
          <>
            <StatCard k="brands" value={counts.brands} to="/brands" />
            <StatCard k="projects" value={counts.projects} to="/projects" />
            <StatCard k="runs" value={counts.runs_30d} sub={runs30dSub} to="/projects" />
            <StatCard k="findings" value={counts.findings_high_conf_30d} to="/findings" />
          </>
        )}
      </section>

      {credits && credits.monthly_allowance > 0 ? (
        <section
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          data-testid="dashboard-credits-card"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Coins className="h-4 w-4" strokeWidth={1.75} />
                {t("credits:dashboard.card_title", { ns: "credits", defaultValue: "Credits" })}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="font-display text-3xl font-semibold tabular-nums text-slate-900"
                  data-testid="dashboard-credits-balance"
                >
                  {(credits.current_balance || 0).toLocaleString(lang)}
                </span>
                <span className="text-sm text-slate-500">
                  / {(credits.monthly_allowance || 0).toLocaleString(lang)}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {t("credits:dashboard.card_subtitle", {
                  ns: "credits",
                  remaining: (credits.current_balance || 0).toLocaleString(lang),
                  allowance: (credits.monthly_allowance || 0).toLocaleString(lang),
                })}
              </p>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 sm:max-w-md">
                <div
                  className="h-full rounded-full bg-indigo-500"
                  style={{
                    width: `${
                      credits.monthly_allowance
                        ? Math.min(
                            100,
                            Math.round(
                              ((credits.monthly_allowance - credits.current_balance) /
                                credits.monthly_allowance) *
                                100
                            )
                          )
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <Link
              to="/settings/credits"
              data-testid="dashboard-credits-manage"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              {t("credits:dashboard.card_cta", { ns: "credits", defaultValue: "Manage" })}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Link>
          </div>
        </section>
      ) : null}

      {/* Recent runs + top findings */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card testId="dashboard-recent-runs">
          <PanelHeader
            title={t("dashboard:panels.recentRuns")}
            action={
              <Link to="/projects" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                {t("common:viewAll")}
              </Link>
            }
          />
          {loading ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <SkeletonLine key={i} className="h-10 w-full" />)}</div>
          ) : (summary?.recent_runs?.length ?? 0) === 0 ? (
            <EmptyHint testId="dashboard-recent-runs-empty">{t("dashboard:empty.runs")}</EmptyHint>
          ) : (
            <ul className="divide-y divide-slate-100">
              {summary.recent_runs.map((r) => (
                <li
                  key={r.id}
                  data-testid={`dashboard-recent-run-${r.id}`}
                  className="flex cursor-pointer items-center gap-3 py-2.5 transition-colors hover:bg-slate-50"
                  onClick={() => navigate(`/runs/${r.id}`)}
                >
                  <StatusPill status={r.status} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{r.project_name || "—"}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      {r.brand_name} · {r.type} · {relativeTime(r.started_at || r.completed_at, lang)}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-slate-500">
                    {r.evidence_count} {t("dashboard:units.evidence")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card testId="dashboard-top-findings">
          <PanelHeader
            title={t("dashboard:panels.topFindings")}
            action={
              <Link to="/findings" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                {t("common:viewAll")}
              </Link>
            }
          />
          {loading ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <SkeletonLine key={i} className="h-12 w-full" />)}</div>
          ) : (summary?.top_findings?.length ?? 0) === 0 ? (
            <EmptyHint testId="dashboard-top-findings-empty">{t("dashboard:empty.findings")}</EmptyHint>
          ) : (
            <ul className="space-y-2">
              {summary.top_findings.map((f) => (
                <li
                  key={f.id}
                  data-testid={`dashboard-top-finding-${f.id}`}
                  className="rounded-lg border border-slate-100 px-3 py-2 transition-colors hover:bg-slate-50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="line-clamp-2 text-sm font-medium text-slate-900">{f.theme_label || "—"}</p>
                    <ReliabilityBadge score={f.confidence} />
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-slate-500">
                    {f.project_name} · {f.evidence_count} {t("dashboard:units.evidence")} · {f.language?.toUpperCase()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Top domains + activity */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card testId="dashboard-top-domains">
          <PanelHeader title={t("dashboard:panels.topDomains")} />
          {loading ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <SkeletonLine key={i} className="h-8 w-full" />)}</div>
          ) : (summary?.top_domains?.length ?? 0) === 0 ? (
            <EmptyHint testId="dashboard-top-domains-empty">{t("dashboard:empty.domains")}</EmptyHint>
          ) : (
            <ul className="space-y-1.5">
              {summary.top_domains.map((d) => (
                <li
                  key={d.domain}
                  data-testid={`dashboard-domain-${d.domain}`}
                  className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-50"
                >
                  <img
                    src={`https://www.google.com/s2/favicons?sz=32&domain=${d.domain}`}
                    alt=""
                    className="h-4 w-4 rounded"
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{d.domain}</span>
                  <span className="text-xs text-slate-500">{d.count}</span>
                  <ReliabilityBadge score={d.avg_reliability} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card testId="dashboard-activity">
          <PanelHeader title={t("dashboard:panels.activity")} />
          {loading ? (
            <div className="space-y-3">{[0, 1, 2, 3].map((i) => <SkeletonLine key={i} className="h-8 w-full" />)}</div>
          ) : (activity?.length ?? 0) === 0 ? (
            <EmptyHint testId="dashboard-activity-empty">{t("dashboard:empty.activity")}</EmptyHint>
          ) : (
            <ul className="space-y-2">
              {activity.slice(0, 10).map((a) => {
                const actor = a.actor?.name || t("dashboard:activity.system");
                const verb = t([`dashboard:activity.verbs.${a.type}`, "dashboard:activity.verbs.default"]);
                const label = a.target?.label || a.target?.id?.slice(0, 8) || "";
                return (
                  <li key={a.id} data-testid={`dashboard-activity-${a.id}`} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                    <p className="text-slate-700">
                      <span className="font-medium text-slate-900">{actor}</span>{" "}
                      {verb}{" "}
                      {label ? <span className="font-medium text-slate-900">&ldquo;{label}&rdquo;</span> : null}
                      <span className="ml-1 text-[11px] text-slate-400">· {relativeTime(a.created_at, lang)}</span>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>

      {/* Admin shortcuts */}
      {canManage ? (
        <section
          data-testid="dashboard-shortcuts"
          className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 md:grid-cols-2"
        >
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {t("dashboard:shortcuts.title")}
            </p>
            <h3 className="mt-1 font-display text-base font-semibold tracking-tight text-slate-900">
              {t("dashboard:shortcuts.openSettings")}
            </h3>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Link
              to="/settings/tenant"
              data-testid="dashboard-open-tenant-settings"
              className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              {t("dashboard:shortcuts.openSettings")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Link>
            <Link
              to="/settings/users"
              data-testid="dashboard-open-users-settings"
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
            >
              {t("dashboard:shortcuts.inviteUser")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}
